<template>
  <main class="module-page">
    <div class="module-shell">
      <template v-if="moduleItem">
        <MarketingToolHeader
          :eyebrow="headerEyebrow"
          :title="moduleItem.title"
          :description="moduleItem.description || defaultDescription"
          :icon="moduleItem.icon || 'mdi-school-outline'">
          <template v-slot:actions>
            <v-btn icon class="icon-action academy-local-back" :aria-label="backLabel" :title="backLabel" @click="$router.push(backPath)">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="tier-badge" :style="{ '--tier-color': tierColor, '--tier-background': tierBackground }">
              <v-icon>{{ isLeadershipModule ? "mdi-account-tie-outline" : "mdi-medal-outline" }}</v-icon>
              {{ tierLabel }}
            </span>
          </template>
        </MarketingToolHeader>

        <section class="metric-grid" aria-label="Modul Übersicht">
          <v-card dark flat class="metric-card">
            <span class="metric-icon icon-green"><v-icon>mdi-play-circle-outline</v-icon></span>
            <div>
              <span>Lektionen</span><strong>{{ (moduleItem.lessons && moduleItem.lessons.length) || 0 }}</strong
              ><small>Lernschritte</small>
            </div>
          </v-card>
          <v-card dark flat class="metric-card">
            <span class="metric-icon icon-blue"><v-icon>mdi-clock-outline</v-icon></span>
            <div>
              <span>Dauer</span><strong>{{ moduleItem.estimatedMinutes || 0 }}</strong
              ><small>Minuten</small>
            </div>
          </v-card>
          <v-card dark flat class="metric-card">
            <span class="metric-icon icon-amber"><v-icon>mdi-toolbox-outline</v-icon></span>
            <div>
              <span>Materialien</span><strong>{{ (moduleItem.toolbox && moduleItem.toolbox.length) || 0 }}</strong
              ><small>Downloads &amp; Notizen</small>
            </div>
          </v-card>
          <v-card dark flat class="metric-card progress-metric">
            <span class="metric-icon icon-green"><v-icon>mdi-chart-donut</v-icon></span>
            <div class="metric-progress-copy">
              <span>Fortschritt</span>
              <strong>{{ getOverallProgress() }}%</strong>
              <v-progress-linear :value="getOverallProgress()" color="#009640" background-color="rgba(255,255,255,0.08)" height="6" rounded />
            </div>
          </v-card>
        </section>

        <v-card v-if="moduleItem.longDescription" dark flat class="content-surface learn-surface">
          <div class="surface-header">
            <span class="surface-icon"><v-icon>mdi-lightbulb-on-outline</v-icon></span>
            <div>
              <div class="surface-eyebrow">Lernziele &amp; Inhalt</div>
              <h2>Was lernst du?</h2>
            </div>
          </div>
          <div class="long-description" v-html="formattedLongDescription"></div>
        </v-card>

        <v-card dark flat class="content-surface lesson-surface">
          <div class="surface-header surface-header-row">
            <div class="surface-title-group">
              <span class="surface-icon"><v-icon>mdi-format-list-numbered</v-icon></span>
              <div>
                <div class="surface-eyebrow">Dein Lernweg</div>
                <h2>Lektionen</h2>
                <p>Die Inhalte werden der Reihe nach freigeschaltet.</p>
              </div>
            </div>
            <span class="surface-count">{{ completedLessonCount }} / {{ (moduleItem.lessons && moduleItem.lessons.length) || 0 }} erledigt</span>
          </div>

          <div class="lesson-list">
            <button
              v-for="(lesson, index) in moduleItem.lessons"
              :key="lesson._id"
              type="button"
              class="lesson-item"
              :class="{ locked: !isLessonUnlocked(index), completed: isLessonCompleted(lesson._id) }"
              :disabled="!isLessonUnlocked(index)"
              @click="openLesson(lesson, index)">
              <span class="lesson-state">
                <v-icon>{{ isLessonCompleted(lesson._id) ? "mdi-check" : isLessonUnlocked(index) ? "mdi-play" : "mdi-lock-outline" }}</v-icon>
              </span>
              <span class="lesson-copy">
                <small>Lektion {{ index + 1 }}</small>
                <strong>{{ lesson.title }}</strong>
                <span>
                  <v-icon>{{ getIconForType(lesson.contentType) }}</v-icon>
                  {{ lesson.contentType }} · {{ lesson.estimatedMinutes || 0 }} Min
                </span>
              </span>
              <v-icon class="lesson-arrow">mdi-chevron-right</v-icon>
            </button>

            <button
              v-if="hasQuizEvaluation"
              type="button"
              class="lesson-item quiz-item"
              :class="{ locked: !isQuizUnlocked() && !isQuizCompleted(), completed: isQuizCompleted() }"
              :disabled="!isQuizUnlocked()"
              @click="openQuiz">
              <span class="lesson-state quiz-state">
                <v-icon>{{ isQuizCompleted() ? "mdi-trophy-outline" : isQuizUnlocked() ? "mdi-help-circle-outline" : "mdi-lock-outline" }}</v-icon>
              </span>
              <span class="lesson-copy">
                <small>Evaluation</small>
                <strong>Abschlusstest</strong>
                <span>{{ isQuizCompleted() ? "Wissenstest bestanden" : "Teste dein Wissen aus allen Lektionen" }}</span>
              </span>
              <v-icon class="lesson-arrow">mdi-chevron-right</v-icon>
            </button>
          </div>
        </v-card>

        <v-card v-if="hasVideoEvaluation && !isQuizUnlocked() && !evaluationCompleted" dark flat class="content-surface evaluation-locked">
          <span class="evaluation-lock-icon"><v-icon>mdi-lock-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">Evaluation</div>
            <h2>Video-Abgabe noch gesperrt</h2>
            <p>Schliesse zuerst alle Lektionen ab. Danach kannst du dein Video aufnehmen oder auswählen.</p>
          </div>
        </v-card>

        <LeadershipVideoSubmission
          v-else-if="hasVideoEvaluation"
          :module-id="moduleItem._id"
          :module-completed="Boolean(progressEntry && progressEntry.completedAt)"
          :instructions="moduleItem.evaluationInstructions || undefined"
          @submitted="loadData" />

        <v-card v-if="hasDocumentEvaluation && !isQuizUnlocked() && !evaluationCompleted" dark flat class="content-surface evaluation-locked">
          <span class="evaluation-lock-icon"><v-icon>mdi-lock-outline</v-icon></span>
          <div>
            <div class="surface-eyebrow">Praxisabschluss</div>
            <h2>Praxisaufgabe noch gesperrt</h2>
            <p>Schliesse zuerst alle Lektionen ab. Danach kannst du deine ausgefüllte Aufgabe als PDF einreichen.</p>
          </div>
        </v-card>

        <PracticeSubmission
          v-else-if="hasDocumentEvaluation"
          :module-id="moduleItem._id"
          :module-completed="Boolean(progressEntry && progressEntry.completedAt)"
          :instructions="moduleItem.evaluationInstructions || undefined"
          @submitted="loadData" />

        <v-card v-if="moduleItem.toolbox && moduleItem.toolbox.length" dark flat class="content-surface toolbox-surface">
          <div class="surface-header surface-header-row">
            <div class="surface-title-group">
              <span class="surface-icon"><v-icon>mdi-toolbox-outline</v-icon></span>
              <div>
                <div class="surface-eyebrow">Zum Vertiefen</div>
                <h2>Toolbox &amp; Materialien</h2>
                <p>Öffne oder lade die Zusatzmaterialien zu diesem Modul herunter.</p>
              </div>
            </div>
            <span class="surface-count">{{ moduleItem.toolbox.length }} Materialien</span>
          </div>

          <div class="toolbox-grid">
            <article v-for="item in moduleItem.toolbox" :key="item._id" class="toolbox-item">
              <div class="toolbox-top">
                <span class="toolbox-icon" :style="{ color: categoryColor(item.category), background: categoryBg(item.category) }">
                  <v-icon>{{ categoryIcon(item.category) }}</v-icon>
                </span>
                <div>
                  <h3>{{ item.title || item.originalName }}</h3>
                  <p>
                    <span>{{ item.category }}</span>
                    <template v-if="item.size"> · {{ formatSize(item.size) }}</template>
                  </p>
                </div>
              </div>

              <button v-if="item.category === 'image'" type="button" class="item-preview" aria-label="Bildvorschau öffnen" @click="openToolbox(item)">
                <SecuredMedia :src="toolboxPath(item)" media-type="image" />
                <span><v-icon>mdi-magnify-plus-outline</v-icon></span>
              </button>
              <p v-else-if="item.category === 'text'" class="text-preview">{{ item.textContent }}</p>

              <div v-if="item.category !== 'text'" class="toolbox-actions">
                <v-btn text color="#58d388" class="tool-action" @click="openToolbox(item)"> <v-icon left>mdi-eye-outline</v-icon>Ansehen </v-btn>
                <v-btn
                  v-if="item.category !== 'video'"
                  text
                  color="#d4d4d8"
                  class="tool-action"
                  :loading="downloadingId === item._id"
                  @click="downloadItem(item)">
                  <v-icon left>mdi-download-outline</v-icon>Download
                </v-btn>
              </div>
            </article>
          </div>
        </v-card>

        <ModuleCompletionAction
          v-if="showCompletionAction"
          :module-id="moduleItem._id"
          :progress="progressEntry"
          :lessons-complete="allLessonsCompleted"
          @completed="handleCompletion" />

        <v-dialog
          v-model="previewDialog.show"
          max-width="56.25rem"
          dark
          scrollable
          :fullscreen="$vuetify.breakpoint.xsOnly"
          content-class="academy-preview-dialog">
          <v-card v-if="previewDialog.item" dark class="preview-card">
            <div class="preview-header">
              <span
                class="toolbox-icon"
                :style="{ color: categoryColor(previewDialog.item.category), background: categoryBg(previewDialog.item.category) }">
                <v-icon>{{ categoryIcon(previewDialog.item.category) }}</v-icon>
              </span>
              <div>
                <div class="surface-eyebrow">Materialvorschau</div>
                <h2>{{ previewDialog.item.title || previewDialog.item.originalName }}</h2>
              </div>
              <v-btn
                v-if="previewDialog.item.category !== 'video'"
                icon
                class="icon-action"
                aria-label="Herunterladen"
                :loading="downloadingId === previewDialog.item._id"
                @click="downloadItem(previewDialog.item)">
                <v-icon>mdi-download-outline</v-icon>
              </v-btn>
              <v-btn icon class="icon-action" aria-label="Vorschau schliessen" @click="previewDialog.show = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
            <v-card-text class="preview-body">
              <SecuredMedia
                :src="toolboxPath(previewDialog.item)"
                :media-type="previewDialog.item.category"
                :stream-playback="previewDialog.item.category === 'video'"
                :download-name="previewDialog.item.originalName"
                :show-app-download-action="false"
                eager />
            </v-card-text>
          </v-card>
        </v-dialog>
      </template>

      <div v-else class="loading-surface">
        <v-progress-circular v-if="loading" indeterminate color="#58d388" size="48" width="4" />
        <template v-else>
          <span><v-icon>mdi-alert-circle-outline</v-icon></span>
          <h2>Modul nicht verfügbar</h2>
          <p>Das Modul konnte nicht geladen werden.</p>
          <v-btn outlined color="#58d388" class="back-action" :to="backPath">{{ backLabel }}</v-btn>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import api from "@/api"
import SecuredMedia from "./SecuredMedia.vue"
import ModuleCompletionAction from "./ModuleCompletionFeedback.vue"
import LeadershipVideoSubmission from "./leadership/LeadershipVideoSubmission.vue"
import PracticeSubmission from "./PracticeSubmission.vue"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

export default {
  name: "ModuleDetail",
  components: { SecuredMedia, ModuleCompletionAction, LeadershipVideoSubmission, PracticeSubmission, MarketingToolHeader },
  data() {
    return {
      moduleItem: null,
      progressEntry: null,
      loading: false,
      previewDialog: { show: false, item: null },
      downloadingId: null,
    }
  },
  computed: {
    isLeadershipModule() {
      return Boolean((this.moduleItem && this.moduleItem.track === "leadership") || this.$route.query.from === "leadership")
    },
    evaluationType() {
      if (!this.moduleItem) return "none"
      return (
        this.moduleItem.evaluationType || (this.moduleItem.evaluation && this.moduleItem.evaluation.type) || (this.moduleItem.quiz ? "quiz" : "none")
      )
    },
    hasVideoEvaluation() {
      return ["video", "video_submission"].includes(this.evaluationType)
    },
    hasDocumentEvaluation() {
      return this.evaluationType === "document_submission"
    },
    hasAcknowledgementEvaluation() {
      return this.evaluationType === "acknowledgement"
    },
    hasQuizEvaluation() {
      return Boolean(this.moduleItem && this.moduleItem.quiz && this.evaluationType === "quiz")
    },
    hasEvaluation() {
      return ["quiz", "video", "video_submission", "document_submission", "acknowledgement"].includes(this.evaluationType)
    },
    allLessonsCompleted() {
      return Boolean(this.moduleItem && this.moduleItem.lessons && this.moduleItem.lessons.every((lesson) => this.isLessonCompleted(lesson._id)))
    },
    evaluationCompleted() {
      if (!this.progressEntry) return false
      if (this.progressEntry.completedAt) return true
      return Boolean(this.progressEntry.evaluationType === this.evaluationType && this.progressEntry.evaluationCompletedAt)
    },
    showCompletionAction() {
      return this.hasAcknowledgementEvaluation && (Boolean(this.progressEntry && this.progressEntry.completedAt) || this.allLessonsCompleted)
    },
    backPath() {
      return this.isLeadershipModule ? "/akademie/leadership/coaching" : "/akademie/suite"
    },
    backLabel() {
      return this.isLeadershipModule ? "Zurück zu Leadership Coaching" : "Zurück zur Learning Suite"
    },
    headerEyebrow() {
      return this.isLeadershipModule ? "Leadership Coaching" : `${this.tierLabel} Lernmodul`
    },
    defaultDescription() {
      if (this.hasAcknowledgementEvaluation) return "Bearbeite alle Lektionen und schliesse das Modul mit deiner Bestätigung ab."
      if (this.hasDocumentEvaluation) return "Bearbeite alle Lektionen und reiche danach deine Praxisaufgabe als PDF zur Prüfung ein."
      return this.isLeadershipModule
        ? "Arbeite die Inhalte in deinem Tempo durch und schliesse das Modul mit der Evaluation ab."
        : "Bearbeite die Lektionen in deinem Tempo und schliesse das Modul mit dem Wissenstest ab."
    },
    tierLabel() {
      if (this.isLeadershipModule) return "Leadership"
      if (!this.moduleItem || !this.moduleItem.tier) return "Akademie"
      return this.moduleItem.tier.charAt(0).toUpperCase() + this.moduleItem.tier.slice(1)
    },
    tierColor() {
      const colors = { bronze: "#d89557", silber: "#d4d4d8", gold: "#f4c95d" }
      return (this.moduleItem && colors[this.moduleItem.tier]) || "#58d388"
    },
    tierBackground() {
      const backgrounds = {
        bronze: "rgba(205, 127, 50, 0.14)",
        silber: "rgba(192, 192, 192, 0.12)",
        gold: "rgba(255, 215, 0, 0.12)",
      }
      return (this.moduleItem && backgrounds[this.moduleItem.tier]) || "rgba(0, 150, 64, 0.12)"
    },
    completedLessonCount() {
      if (!this.moduleItem || !this.moduleItem.lessons) return 0
      return this.moduleItem.lessons.filter((lesson) => this.isLessonCompleted(lesson._id)).length
    },
    formattedLongDescription() {
      if (!this.moduleItem || !this.moduleItem.longDescription) return ""
      const safeText = this.moduleItem.longDescription.replace(/[<>&]/g, (character) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[character]))
      return safeText
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [moduleItem, progressEntries] = await Promise.all([api.akademie.fetchModule(this.$route.params.id), api.akademie.fetchProgress()])
        this.moduleItem = moduleItem
        if (progressEntries && Array.isArray(progressEntries)) {
          this.progressEntry =
            progressEntries.find((entry) => entry.module === this.$route.params.id || (entry.module && entry.module._id === this.$route.params.id)) ||
            null
        }
      } catch (error) {
        console.error("Fehler beim Laden des Moduls:", error)
      } finally {
        this.loading = false
      }
    },
    getIconForType(type) {
      const icons = {
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        text: "mdi-text-box-outline",
        image: "mdi-image-outline",
        pdf: "mdi-file-pdf-box",
        file: "mdi-file-outline",
      }
      return icons[type] || "mdi-file-outline"
    },
    isLessonCompleted(lessonId) {
      if (!this.progressEntry || !this.progressEntry.lessons) return false
      const lessonProgress = this.progressEntry.lessons.find((entry) => entry.lesson === lessonId || (entry.lesson && entry.lesson._id === lessonId))
      return Boolean(lessonProgress && lessonProgress.completedAt != null)
    },
    isLessonUnlocked(index) {
      if (index === 0) return true
      const previousLessonId = this.moduleItem.lessons[index - 1]._id
      return this.isLessonCompleted(previousLessonId)
    },
    isQuizUnlocked() {
      if (!this.moduleItem || !this.moduleItem.lessons) return false
      return this.moduleItem.lessons.every((lesson) => this.isLessonCompleted(lesson._id))
    },
    isQuizCompleted() {
      return this.hasQuizEvaluation && this.evaluationCompleted
    },
    getOverallProgress() {
      if (!this.progressEntry || !this.moduleItem || !this.moduleItem.lessons) return 0
      if (this.progressEntry.completedAt) return 100
      let completed = this.moduleItem.lessons.filter((lesson) => this.isLessonCompleted(lesson._id)).length
      const evaluationSteps = this.hasEvaluation ? 1 : 0
      if (evaluationSteps && this.evaluationCompleted) completed += 1
      const total = this.moduleItem.lessons.length + evaluationSteps
      if (!total) return 0
      return Math.floor((completed / total) * 100)
    },
    handleCompletion(progress) {
      if (progress) this.progressEntry = progress
      else this.loadData()
    },
    openLesson(lesson, index) {
      if (this.isLessonUnlocked(index)) {
        this.$router.push({
          path: `/akademie/modules/${this.moduleItem._id}/lessons/${lesson._id}`,
          query: this.isLeadershipModule ? { from: "leadership" } : {},
        })
      }
    },
    openQuiz() {
      if (this.isQuizUnlocked()) {
        this.$router.push({
          path: `/akademie/modules/${this.moduleItem._id}/quiz`,
          query: this.isLeadershipModule ? { from: "leadership" } : {},
        })
      }
    },
    openToolbox(item) {
      this.previewDialog = { show: true, item }
    },
    toolboxPath(item) {
      return item.category === "video"
        ? api.akademie.getToolboxItemPlaybackPath(this.moduleItem._id, item._id)
        : api.akademie.getToolboxItemPath(this.moduleItem._id, item._id)
    },
    async downloadItem(item) {
      this.downloadingId = item._id
      try {
        await api.akademie.downloadToolboxItem(this.moduleItem._id, item._id, item.originalName || item.title || "datei")
      } catch (error) {
        console.error("download error", error)
      } finally {
        this.downloadingId = null
      }
    },
    categoryIcon(category) {
      const icons = {
        image: "mdi-image-outline",
        video: "mdi-video-outline",
        audio: "mdi-music-note-outline",
        pdf: "mdi-file-pdf-box",
        text: "mdi-text-box-outline",
        file: "mdi-file-outline",
      }
      return icons[category] || "mdi-file-outline"
    },
    categoryColor(category) {
      const colors = { image: "#60a5fa", video: "#fb7185", audio: "#c084fc", pdf: "#f87171", text: "#58d388", file: "#d4d4d8" }
      return colors[category] || "#d4d4d8"
    },
    categoryBg(category) {
      const backgrounds = {
        image: "rgba(96, 165, 250, 0.12)",
        video: "rgba(251, 113, 133, 0.12)",
        audio: "rgba(192, 132, 252, 0.12)",
        pdf: "rgba(248, 113, 113, 0.12)",
        text: "rgba(0, 150, 64, 0.12)",
        file: "rgba(212, 212, 216, 0.1)",
      }
      return backgrounds[category] || "rgba(255,255,255,0.04)"
    },
    formatSize(bytes) {
      if (!bytes) return ""
      if (bytes < 1024) return bytes + " B"
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
      return (bytes / 1024 / 1024).toFixed(1) + " MB"
    },
  },
}
</script>

<style scoped>
.module-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.module-shell {
  width: 100%;
  max-width: 78rem;
  margin: 0 auto;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.tier-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--tier-color);
  border-radius: 0.8125rem;
  color: var(--tier-color);
  background: var(--tier-background);
  font-size: 0.875rem;
  font-weight: 750;
}

.tier-badge .v-icon {
  color: currentColor;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
}

.metric-icon {
  display: grid;
  flex: 0 0 2.625rem;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.metric-icon .v-icon,
.surface-icon .v-icon,
.toolbox-icon .v-icon {
  color: currentColor;
}

.icon-green {
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.icon-blue {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.11);
}

.icon-amber {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.metric-card > div {
  display: grid;
  min-width: 0;
  flex: 1 1 auto;
}

.metric-card > div > span {
  color: #d4d4d8;
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  color: var(--muted);
  font-size: 0.75rem;
}

.metric-progress-copy .v-progress-linear {
  margin-top: 0.375rem;
}

.content-surface {
  margin-top: 1rem;
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.evaluation-locked {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.evaluation-lock-icon {
  display: grid;
  flex: 0 0 2.75rem;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.875rem;
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.06);
}

.evaluation-lock-icon .v-icon {
  color: currentColor;
}

.evaluation-locked h2 {
  margin: 0.1875rem 0 0.25rem;
  color: #ffffff;
  font-size: 1.125rem;
}

.evaluation-locked p {
  margin: 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.surface-header,
.surface-title-group,
.preview-header,
.toolbox-top {
  display: flex;
  align-items: center;
}

.surface-header,
.surface-title-group {
  gap: 0.75rem;
}

.surface-header-row {
  justify-content: space-between;
  gap: 1rem;
}

.surface-icon,
.toolbox-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 0.8125rem;
}

.surface-icon {
  width: 2.625rem;
  height: 2.625rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2,
.preview-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
  line-height: 1.3;
}

.surface-header p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.module-page ::v-deep .marketing-tool-header .header-copy p,
.long-description,
.text-preview {
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.long-description {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.875rem;
  color: #d4d4d8;
  background: rgba(0, 0, 0, 0.12);
  font-size: 1rem;
  line-height: 1.75;
}

.surface-count {
  flex: 0 0 auto;
  min-height: 1.75rem;
  padding: 0.3125rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.lesson-list {
  display: grid;
  gap: 0.625rem;
  margin-top: 1rem;
}

.lesson-item {
  display: grid;
  width: 100%;
  min-height: 5rem;
  grid-template-columns: 2.75rem minmax(0, 1fr) 1.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.9375rem;
  outline: none;
  color: var(--text);
  background: var(--surface);
  cursor: pointer;
  text-align: left;
  transition: border-color 160ms ease, background-color 160ms ease;
}

.lesson-item:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.lesson-item.completed {
  border-color: rgba(0, 150, 64, 0.28);
}

.lesson-item:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.lesson-state {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.lesson-item.locked .lesson-state {
  color: #8f9095;
  background: rgba(255, 255, 255, 0.055);
}

.quiz-state {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.lesson-state .v-icon {
  color: currentColor;
}

.lesson-copy {
  display: grid;
  min-width: 0;
}

.lesson-copy small {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 750;
}

.lesson-copy strong {
  overflow: hidden;
  color: #ffffff;
  font-size: 0.9375rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lesson-copy > span {
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
  text-transform: capitalize;
}

.lesson-copy > span .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.lesson-arrow {
  color: #8f9095 !important;
}

.toolbox-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.toolbox-item {
  display: flex;
  min-height: 12rem;
  flex-direction: column;
  padding: 0.875rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: var(--surface);
}

.toolbox-top {
  align-items: flex-start;
  gap: 0.75rem;
}

.toolbox-icon {
  width: 2.625rem;
  height: 2.625rem;
}

.toolbox-top > div {
  min-width: 0;
}

.toolbox-top h3 {
  margin: 0;
  overflow: hidden;
  color: var(--text);
  font-size: 0.9375rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.toolbox-top p {
  margin: 0.1875rem 0 0;
  color: var(--muted);
  font-size: 0.75rem;
  text-transform: capitalize;
}

.item-preview {
  position: relative;
  min-height: 7rem;
  margin-top: 0.75rem;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 0.75rem;
  outline: none;
  background: rgba(0, 0, 0, 0.18);
  cursor: pointer;
}

.item-preview ::v-deep .secured-media-content {
  max-height: 8rem;
  object-fit: cover;
}

.item-preview > span {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #ffffff;
  background: rgba(0, 0, 0, 0.34);
  opacity: 0;
  transition: opacity 160ms ease;
}

.item-preview:hover > span,
.item-preview:focus-visible > span {
  opacity: 1;
}

.text-preview {
  display: -webkit-box;
  margin: 0.75rem 0 0;
  overflow: hidden;
  color: #c7c7cb;
  font-size: 0.875rem;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
}

.toolbox-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: auto -0.5rem -0.5rem;
  padding-top: 0.75rem;
}

.tool-action {
  min-height: 2.75rem !important;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.preview-card {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  display: flex;
  max-height: calc(100vh - 2rem);
  max-height: calc(100dvh - 2rem);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1.25rem !important;
  background: #1c1c1d !important;
}

.preview-header {
  flex: 0 0 auto;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
}

.preview-header > div {
  min-width: 0;
  flex: 1 1 auto;
}

.preview-header h2 {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-body {
  min-height: 0;
  flex: 1 1 auto;
  padding: 1rem !important;
  overflow-y: auto;
}

.loading-surface {
  display: flex;
  min-height: 70vh;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
  text-align: center;
}

.loading-surface > span {
  display: grid;
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.875rem;
  place-items: center;
  border-radius: 1rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.loading-surface > span .v-icon {
  color: currentColor;
}

.loading-surface h2 {
  margin: 0;
  color: var(--text);
  font-size: 1.25rem;
}

.loading-surface p {
  margin: 0.375rem 0 0;
  font-size: 0.875rem;
}

.back-action {
  min-height: 2.75rem !important;
  margin-top: 1rem;
  border-radius: 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  text-transform: none;
}

@media (hover: hover) and (pointer: fine) {
  .lesson-item:hover:not(:disabled),
  .toolbox-item:hover {
    border-color: var(--border-strong);
    background: var(--surface-strong);
  }
}

@media (max-width: 78.9375rem) {
  .module-page {
    padding: 1.125rem;
  }

  .metric-grid,
  .toolbox-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 37.4375rem) {
  .module-page {
    padding: 0.75rem;
  }

  .metric-grid {
    gap: 0.5rem;
    margin: 0.75rem 0;
  }

  .metric-card {
    min-height: 7rem;
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 0.9375rem !important;
  }

  .metric-icon {
    width: 2.25rem;
    height: 2.25rem;
    flex-basis: 2.25rem;
  }

  .content-surface {
    padding: 0.875rem;
    border-radius: 1.0625rem !important;
  }

  .surface-header-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .surface-count {
    margin-left: 3.375rem;
  }

  .lesson-item {
    grid-template-columns: 2.5rem minmax(0, 1fr) 1.25rem;
    gap: 0.625rem;
  }

  .lesson-state {
    width: 2.5rem;
    height: 2.5rem;
  }

  .toolbox-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .preview-card {
    max-height: 100vh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0 !important;
  }

  .preview-header {
    padding: 0.875rem;
  }

  .preview-header .toolbox-icon {
    display: none;
  }
}
</style>
